import { Link } from "@tanstack/react-router";
import { Lock, Send, Clock, XCircle, Loader2 } from "lucide-react";
import { usePermissions } from "@/lib/permissions";

interface Props {
  /** Type of permission required (changes copy only) */
  required: "create-events" | "manage-venues" | "any-tenant";
  children: React.ReactNode;
}

/**
 * Wrap any page/section that requires elevated permissions.
 * Shows a friendly "request access" UI when the user lacks them.
 *
 * Backend RLS still enforces the actual rules — this is UX only.
 */
export function AccessGate({ required, children }: Props) {
  const perms = usePermissions();

  if (perms.loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!perms.user) {
    return (
      <div className="mx-auto max-w-md px-4 pt-32 text-center">
        <Lock className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-3 text-xl font-bold text-foreground">Iniciá sesión</h1>
        <p className="mt-1 text-sm text-muted-foreground">Necesitás una cuenta para continuar.</p>
        <Link to="/login" className="mt-4 inline-block rounded-xl gradient-primary px-5 py-2.5 text-sm font-bold text-primary-foreground">
          Ingresar
        </Link>
      </div>
    );
  }

  const allowed =
    required === "create-events"
      ? perms.canCreateEventsAnywhere
      : required === "manage-venues"
        ? perms.canManageVenuesAnywhere
        : perms.belongsToAnyTenant || perms.isPlatformAdmin || perms.isLegacyOrganizer;

  if (allowed) return <>{children}</>;

  const titles = {
    "create-events": "Necesitás permisos para crear eventos",
    "manage-venues": "Necesitás permisos para gestionar venues",
    "any-tenant": "No pertenecés a ninguna organización",
  };
  const descriptions = {
    "create-events":
      "Para publicar eventos en ZYNTRA tenés que pertenecer a una organización con permiso de creación. Solicitá acceso y un admin revisará tu pedido.",
    "manage-venues":
      "Crear y editar venues requiere permiso de tu organización. Si ya pertenecés a una, pedile a un admin que active el permiso.",
    "any-tenant":
      "El dashboard de organizador es solo para miembros de organizaciones. Si querés organizar eventos, podés solicitar acceso.",
  };

  return (
    <div className="mx-auto max-w-xl px-4 pb-20 pt-32">
      <div className="rounded-3xl border border-border bg-card p-8 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
          <Lock className="h-6 w-6 text-primary" />
        </div>
        <h1 className="mt-4 text-xl font-bold text-foreground">{titles[required]}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{descriptions[required]}</p>

        {perms.hasPendingRequest ? (
          <div className="mt-6 rounded-xl bg-warning/10 p-4 text-left">
            <p className="flex items-center gap-2 text-sm font-bold text-warning">
              <Clock className="h-4 w-4" /> Tu solicitud está en revisión
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Enviada el{" "}
              {new Date(perms.pendingRequest!.requested_at).toLocaleDateString("es-AR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
              . Te avisaremos cuando un admin la revise.
            </p>
          </div>
        ) : perms.lastRejection ? (
          <div className="mt-6 rounded-xl bg-destructive/10 p-4 text-left">
            <p className="flex items-center gap-2 text-sm font-bold text-destructive">
              <XCircle className="h-4 w-4" /> Solicitud anterior rechazada
            </p>
            {perms.lastRejection.admin_notes && (
              <p className="mt-1 text-xs text-muted-foreground">
                Motivo: {perms.lastRejection.admin_notes}
              </p>
            )}
            <Link
              to="/request-organizer-access"
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground"
            >
              <Send className="h-3 w-3" /> Solicitar nuevamente
            </Link>
          </div>
        ) : (
          <Link
            to="/request-organizer-access"
            className="mt-6 inline-flex items-center gap-2 rounded-xl gradient-primary px-5 py-2.5 text-sm font-bold text-primary-foreground transition-opacity hover:opacity-90"
          >
            <Send className="h-4 w-4" /> Solicitar acceso
          </Link>
        )}

        <Link
          to="/"
          className="mt-3 block text-xs text-muted-foreground hover:text-foreground"
        >
          ← Volver al inicio
        </Link>
      </div>
    </div>
  );
}
